//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  ActiveCreatorsCampaignsListRequest,
  ApplySparkCodeRequest,
  BulkUpdateCreatorsRequest,
  BulkUpdateCreatorsResponse,
  CampaignSparkCodesInfo,
  CampaignWorkflowProductsListRequest,
  CampaignWorkflowProductsListResponse,
  CreatorPortalInviteLinkResponse,
  CreatorPortalLinksResponse,
  CreatorsCampaignListResponse,
  CreatorsCampaignsAddCreatorsRequest,
  CreatorsCampaignsAddCreatorsResponse,
  CreatorsCampaignsAffiliateOrdersRequest,
  CreatorsCampaignsAffiliateOrdersResponse,
  CreatorsCampaignsAffiliatesAggregateStatsResponse,
  CreatorsCampaignsAffiliatesGAConfigurationRequest,
  CreatorsCampaignsAffiliatesGoogleAnalyticsConfigurationResponse,
  CreatorsCampaignsAffiliatesListRequest,
  CreatorsCampaignsAffiliatesListResponse,
  CreatorsCampaignsBasicRequest,
  CreatorsCampaignsDeleteContentRequest,
  CreatorsCampaignsDeleteCreatorsRequest,
  CreatorsCampaignsDeletePendingCreatorsRequest,
  CreatorsCampaignsInfoResponse,
  CreatorsCampaignsMembersListResponse,
  CreatorsCampaignsMembersRequest,
  CreatorsCampaignsMetricsResponse,
  CreatorsCampaignsProposalsListResponse,
  CreatorsCampaignsProposalsRequest,
  CreatorsCampaignsReportRequest,
  CreatorsCampaignsReportResponse,
  CreatorsCampaignsUpdateCreatorMetadataRequest,
  CreatorsCampaignsWorkflowPreviewTokenResponse,
  CreatorsCampaignsWorkflowResponse,
  CreatorsCampaignsWorkflowUpdateRequest,
  DuplicateCreatorsCampaignRequest,
  ExportCreatorPortalLinkCSVRequest,
  GetCampaignSparkCodeRequest,
  GetConnectedImpactPartnersRequest,
  GetConnectedImpactPartnersResponse,
  GetCreatorsCampaignAffiliatesStatsRequest,
  GetCreatorsCampaignsWorkflowRequest,
  GetPartnerMappingsRequest,
  GetPartnerMappingsResponse,
  GetSparkCodeMediaIdsRequest,
  ImpactMappingRequest,
  LinkImpactPartnerResponse,
  LinkTiktokVideoToTTOCampaignRequest,
  LinkTiktokVideoToTTOCampaignResponse,
  RequestSparkCodeRequest,
  SparkCodeMedia,
  SparkCodeMediaResponse,
  TikTokAdAccountsResponse,
  UploadCampaignAssetsRequest,
  UploadCampaignAssetsResponse,
  UploadCreatorMediaRequest,
  UploadCreatorMediaResponse,
  UpsertCreatorsCampaignRequest,
  UpsertCreatorsCampaignResponse,
} from '@apis/creators-campaigns/CreatorsCampaignsApiModel';
import {
  deserializeActiveCreatorsCampaignsList,
  deserializeCampaignSparkCodeResponse,
  deserializeContentApprovalCreatorsList,
  deserializeContentReviewContentsList,
  deserializeCreatorsCampaignsInfo,
  deserializeSendCommentResponse,
} from '@apis/creators-campaigns/CreatorsCampaignsApiUtils';
import {
  ApproveContentRequest,
  ApproveContentResponse,
  ContentApprovalContentResponse,
  ContentApprovalCreatorsListResponse,
  CreatorContentApprovalLinkResponse,
  ExportContentApprovalCreatorsRequest,
  ExportCreatorContentApprovalLinkCSVRequest,
  GetContentApprovalCreatorsListRequest,
  GetCreatorContentApprovalCreatorContentsRequest,
  GetCreatorContentApprovalLinkRequest,
  SendCommentRequest,
  SendCommentResponse,
} from '@apis/creators-campaigns/content-approval/contentApprovalApiModel';
import {
  ImpactWorkflowRequest,
  LinkImpactProgramRequest,
  LinkImpactProgramRequestBody,
  LinkedImpactProgramDto,
  UnlinkCreatorFromImpactPartnerRequest,
  UnlinkCreatorFromImpactPartnerResponse,
} from '@apis/impact/ImpactApiModel';
import { isDefined, isEmptyValue } from '@utils/filterPredicates';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { SerializedArray } from '@utils/types/Serialized';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const CREATOR_CAMPAIGNS_DETAILS_DEFAULT = '/api/v1/campaigns';
const PASSTHROUGH_DEFAULT = '/api/v1/pass-through';
const CLIENT_USERS_DEFAULT = '/api/v1/client-users';

const creatorsCampaignsFetch = (
  endpoint: string,
  init?: RequestInit,
  useFetchHeader?: boolean,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'affable_gateway',
    `${CREATOR_CAMPAIGNS_DETAILS_DEFAULT}${endpoint}`,
    init,
    useFetchHeader,
  );
};

// TODO: PD-250821: Remove this function once all campaign
// endpoints are made available in the gateway
const passthroughFetch = (
  endpoint: string,
  init?: RequestInit,
  useFetchHeader?: boolean,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'affable_gateway',
    `${PASSTHROUGH_DEFAULT}${endpoint}`,
    init,
    useFetchHeader,
  );
};

/**
 * GET /api/v1/campaigns/:campaignSlugName
 *
 * Get Creators Campaigns information by campaignSlugName
 * @param {string} req.campaignSlugName - The unique identifier for the campaign
 */
export const getCreatorsCampaignsInfo = async (
  req: CreatorsCampaignsBasicRequest,
  init?: RequestInit,
): Promise<CreatorsCampaignsInfoResponse> => {
  const { campaignSlugName } = req;
  const res = await creatorsCampaignsFetch(`/${campaignSlugName}`, {
    ...init,
    method: 'GET',
  });
  const campaignInfoResults = await res.json<{
    data: CreatorsCampaignsInfoResponse;
  }>((res) => ({
    data: deserializeCreatorsCampaignsInfo(res.data),
  }));

  return campaignInfoResults.data;
};

/**
 * GET /api/v1/campaigns/summary
 *
 * Get Creators Campaigns Metrics
 */
export const getCreatorsCampaignsMetrics = async (
  init?: RequestInit,
): Promise<CreatorsCampaignsMetricsResponse> => {
  const res = await creatorsCampaignsFetch('/summary', {
    ...init,
    method: 'GET',
  });

  const campaignMetricsResults = await res.json<{
    data: CreatorsCampaignsMetricsResponse;
  }>();

  return campaignMetricsResults.data;
};

/**
 * GET /api/v1/campaigns/:campaignSlugName/workflow
 *
 * Get Creators Campaigns workflow by campaignSlugName
 * @param {string} req.campaignSlugName - The unique identifier for the campaign
 */
export const getCreatorsCampaignsWorkflow = async (
  req: GetCreatorsCampaignsWorkflowRequest,
  init?: RequestInit,
): Promise<CreatorsCampaignsWorkflowResponse> => {
  const { campaignSlugName, ...queryParams } = req;

  const res = await creatorsCampaignsFetch(
    `/${campaignSlugName}/workflow?${makeParams(queryParams)}`,
    {
      ...init,
      method: 'GET',
    },
  );

  const campaignWorkflowResults = await res.json<{
    data: CreatorsCampaignsWorkflowResponse;
  }>((res) => ({
    data: deserializeCreatorsCampaignsInfo(res.data),
  }));

  return campaignWorkflowResults.data;
};

/**
 * PUT /api/v1/campaigns/:campaignSlugName/workflow
 *
 * Update Creators Campaigns workflow by campaignSlugName
 * @param req.assets - The asset List to be uploaded
 * @param req.campaignImage - The campaign image to be uploaded
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.shouldSaveRevision - The flag determines whether to create a new revision version of the campaign workflow (if set to `true`), or a temporary version (if set to `false`).
 * @param req.step - The step in the campaign configuration
 * @param req.workflow - The workflow object to be updated
 *
 */
export const updateCreatorsCampaignsWorkflow = async (
  {
    campaignSlugName,
    shouldSaveRevision,
    step,
    workflow,
  }: CreatorsCampaignsWorkflowUpdateRequest,
  init?: RequestInit,
): Promise<CreatorsCampaignsWorkflowResponse> => {
  const body = JSON.stringify({
    step,
    workflow,
    saveRevision: !!shouldSaveRevision,
  });

  const res = await creatorsCampaignsFetch(`/${campaignSlugName}/workflow`, {
    ...init,
    method: 'PUT',
    body,
  });

  return res.json<CreatorsCampaignsWorkflowResponse>((res) =>
    deserializeCreatorsCampaignsInfo(res),
  );
};

/**
 * GET /api/v1/campaigns/:campaignSlugName/workflow/preview-token
 *
 * Get Creators Campaigns workflow preview token by campaignSlugName
 * @param {string} req.campaignSlugName - The unique identifier for the campaign
 */
export const getCreatorsCampaignsWorkflowPreviewToken = async (
  req: GetCreatorsCampaignsWorkflowRequest,
  init?: RequestInit,
): Promise<CreatorsCampaignsWorkflowPreviewTokenResponse> => {
  const { campaignSlugName } = req;

  const res = await creatorsCampaignsFetch(
    `/${campaignSlugName}/workflow/preview-token`,
    {
      ...init,
      method: 'GET',
    },
  );

  const campaignWorkflowPreviewTokenResults = await res.json<{
    data: CreatorsCampaignsWorkflowPreviewTokenResponse;
  }>();

  return campaignWorkflowPreviewTokenResults.data;
};

/**
 * GET /api/v1/campaigns/:campaignSlugName/products
 *
 * Get Creators Campaign Workflow Products
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.pageNumber - The page number for pagination
 * @param req.pageSize - The number of items per page
 * @param req.searchTerm - The search term for filtering products
 * @param req.sortCriteria - The criteria for sorting the products
 * @param req.sortDirection - The direction for sorting ('asc' or 'desc')
 */
export const getCreatorsCampaignWorkflowProducts = async (
  req: CampaignWorkflowProductsListRequest,
  init?: RequestInit,
): Promise<CampaignWorkflowProductsListResponse> => {
  const { campaignSlugName, ...queryParams } = req;

  const res = await creatorsCampaignsFetch(
    `/${campaignSlugName}/products?${makeParams(queryParams)}`,
    { ...init, method: 'GET' },
  );

  const campaignWorkflowProductsResults = await res.json<{
    data: CampaignWorkflowProductsListResponse;
  }>();

  return campaignWorkflowProductsResults.data;
};

/**
 * GET /api/v1/campaigns/:campaignSlugName/creators
 *
 * Get Creators Campaigns member list by campaignSlugName
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.networks - The platform(s) to filter campaign creators with
 * @param req.pageNumber - The page number for pagination
 * @param req.pageSize - The page size for pagination
 * @param req.searchTerm - The search term for searching of campaign creators
 */
export const getCreatorsCampaignsMembersList = async (
  req: CreatorsCampaignsMembersRequest,
  init?: RequestInit,
): Promise<CreatorsCampaignsMembersListResponse> => {
  const {
    campaignSlugName,
    excludeExtraDetails,
    networks,
    pageNumber,
    pageSize,
    populateWithCreatorProfile,
    searchTerm,
  } = req;
  const params = makeParams({
    excludeExtraDetails,
    networks,
    pageNumber,
    pageSize,
    populateWithCreatorProfile,
    searchTerm,
  });
  const res = await creatorsCampaignsFetch(
    `/${campaignSlugName}/creators?${params}`,
    {
      ...init,
      method: 'GET',
    },
  );
  const campaignMembersListResults = await res.json<{
    data: CreatorsCampaignsMembersListResponse;
  }>();

  return campaignMembersListResults.data;
};

/**
 * GET /api/v1/campaigns/:campaignSlugName/proposals
 *
 * Get Creators Campaigns proposals list by campaignSlugName
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.pageNumber - The page number for pagination
 * @param req.pageSize - The page size for pagination
 * @param req.searchTerm - The search term for searching of campaign proposals
 */
export const getCreatorsCampaignsProposalsList = async (
  req: CreatorsCampaignsProposalsRequest,
  init?: RequestInit,
): Promise<CreatorsCampaignsProposalsListResponse> => {
  const { campaignSlugName, pageNumber, pageSize, searchTerm } = req;
  const params = makeParams({ pageNumber, pageSize, search: searchTerm });
  const res = await creatorsCampaignsFetch(
    `/${campaignSlugName}/proposals?${params}`,
    {
      ...init,
      method: 'GET',
    },
  );

  const campaignProposalsListResults = await res.json<{
    data: CreatorsCampaignsProposalsListResponse;
  }>();

  return campaignProposalsListResults.data;
};

/**
 * POST /api/v1/campaigns/report
 *
 * Retrieves sentiment analysis data for a specific campaign identified by `campaignSlugName`.
 * @param req.campaignSlugName - The unique identifier (slug) for the campaign whose sentiment data is requested.
 * @param req.mediaIds - An array of facebook/media IDs for which the sentiment report is desired.
 * @param req.isAggregation - A boolean flag indicating whether to aggregate the sentiment data across the specified media.
 */
export const getCreatorsCampaignsReport = async (
  req: CreatorsCampaignsReportRequest,
  init?: RequestInit,
): Promise<CreatorsCampaignsReportResponse> => {
  const { campaignSlugName } = req;
  const params = makeParams({ campaignSlugName });
  const res = await creatorsCampaignsFetch(`/report?${params}`, {
    ...init,
    method: 'POST',
    body: JSON.stringify(req),
  });
  const campaignSentimentsDataResults = await res.json<{
    data: CreatorsCampaignsReportResponse;
  }>();
  return campaignSentimentsDataResults.data;
};

/**
 * POST /api/v1/campaigns/:campaignSlugName/creators
 *
 * Add Creators to Campaign by username and platform
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.creators - List of creators to be added to the campaign as type
 */
export const addCreatorsToCreatorsCampaigns = async (
  req: CreatorsCampaignsAddCreatorsRequest,
  init?: RequestInit,
): Promise<CreatorsCampaignsAddCreatorsResponse> => {
  const { campaignSlugName } = req;
  const res = await creatorsCampaignsFetch(`/${campaignSlugName}/creators`, {
    ...init,
    method: 'POST',
    body: JSON.stringify({ creators: req.creators }),
  });
  const addCreatorsToCampaignResults = await res.json<{
    data: CreatorsCampaignsAddCreatorsResponse;
  }>();
  return addCreatorsToCampaignResults.data;
};

/**
 * PATCH /api/v1/campaigns/:campaignSlugName/creators
 *
 * Update a single Campaign Creator's metadata by username and platform
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.creator - Creator's data to be updated
 */
export const updateCreatorsCampaignsCreatorMetadata = async (
  req: CreatorsCampaignsUpdateCreatorMetadataRequest,
  init?: RequestInit,
): Promise<void> => {
  const { campaignSlugName } = req;
  await creatorsCampaignsFetch(`/${campaignSlugName}/creators`, {
    ...init,
    method: 'PATCH',
    body: JSON.stringify(req.creator),
  });
};

/**
 * PATCH /api/v1/campaigns/:campaignSlugName/creators/pending/delete
 *
 * Delete Pending Creators of Campaign by username
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.usernames - List of creators to be added to the campaign as type
 */
export const deletePendingCreators = async (
  req: CreatorsCampaignsDeletePendingCreatorsRequest,
  init?: RequestInit,
): Promise<void> => {
  const { campaignSlugName, usernames } = req;
  await creatorsCampaignsFetch(`/${campaignSlugName}/creators/pending/delete`, {
    ...init,
    method: 'PATCH',
    body: JSON.stringify({ usernames }),
  });
};

/**
 * POST /api/v1/campaigns
 *
 * Create Creators Client Campaign
 * @param req.campaignName - The name of the campaign
 * @param req.hashtags - The list of hashtags for the campaign (optional)
 * @param req.mentions - The list of mentions for the campaign (optional)
 * @param req.startDate - The start date
 * @param req.endDate - The end date (optional)
 */
export const createCreatorsClientCampaign = async (
  req: UpsertCreatorsCampaignRequest,
  init?: RequestInit,
): Promise<UpsertCreatorsCampaignResponse> => {
  const res = await creatorsCampaignsFetch('', {
    ...init,
    body: JSON.stringify(req),
    method: 'POST',
  });

  const createdCreatorsCampaignResponse = await res.json<{
    data: UpsertCreatorsCampaignResponse;
  }>();

  return createdCreatorsCampaignResponse.data;
};

/**
 * PATCH /api/v1/campaigns
 *
 * Create Creators Client Campaign
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.campaignName - The name of the campaign
 * @param req.hashtags - The list of hashtags for the campaign (optional)
 * @param req.mentions - The list of mentions for the campaign (optional)
 * @param req.startDate - The start date
 * @param req.endDate - The end date (optional)
 */
export const updateCreatorsClientCampaign = async (
  req: UpsertCreatorsCampaignRequest,
  init?: RequestInit,
): Promise<UpsertCreatorsCampaignResponse> => {
  const res = await creatorsCampaignsFetch(`/${req.campaignSlugName}`, {
    ...init,
    body: JSON.stringify(req),
    method: 'PATCH',
  });

  const createdCreatorsCampaignResponse = await res.json<{
    data: UpsertCreatorsCampaignResponse;
  }>();

  return createdCreatorsCampaignResponse.data;
};

/**
 * DELETE /api/v1/campaigns/:campaignSlugName
 *
 * Delete Creators Client Campaign
 * @param req.campaignSlugName - The unique identifier for the campaign
 */
export const deleteCreatorsCampaign = async (
  req: CreatorsCampaignsBasicRequest,
  init?: RequestInit,
): Promise<void> => {
  await creatorsCampaignsFetch(`/${req.campaignSlugName}`, {
    ...init,
    method: 'DELETE',
  });
};

/**
 * POST /api/v1/campaigns/:campaignSlugName/duplicate
 *
 * Duplicate Creators Client Campaign
 * @param req.originalCampaignSlugName - The unique identifier for the original campaign
 * @param req.campaignName - The name of the campaign
 * @param req.sectionsToDuplicate - The list of sections to duplicate
 */
export const duplicateCreatorsCampaign = async (
  req: DuplicateCreatorsCampaignRequest,
  init?: RequestInit,
): Promise<UpsertCreatorsCampaignResponse> => {
  const res = await creatorsCampaignsFetch(
    `/${req.originalCampaignSlugName}/duplicate`,
    {
      ...init,
      body: JSON.stringify(req),
      method: 'POST',
    },
  );

  const createdCreatorsCampaignResponse = await res.json<{
    data: UpsertCreatorsCampaignResponse;
  }>();

  return createdCreatorsCampaignResponse.data;
};

/**
 * PATCH /api/v1/campaigns/:campaignSlugName/creators/delete
 *
 * Delete Pending Creators of Campaign by username
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.creators - List of creators to be added to the campaign as type
 */
export const deleteCreators = async (
  req: CreatorsCampaignsDeleteCreatorsRequest,
  init?: RequestInit,
): Promise<void> => {
  const { campaignSlugName, creators } = req;
  await creatorsCampaignsFetch(`/${campaignSlugName}/creators/delete`, {
    ...init,
    method: 'PATCH',
    body: JSON.stringify({ creators }),
  });
};

/**
 * DELETE /api/v1/campaigns/:campaignSlugName/contents/delete
 *
 * Delete content from a campaign
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.contentIds - List of content IDs to be deleted from the campaign
 */
export const deleteCreatorsCampaignContent = async (
  req: CreatorsCampaignsDeleteContentRequest,
  init?: RequestInit,
): Promise<void> => {
  const { campaignSlugName, contentIds } = req;

  await creatorsCampaignsFetch(`/${campaignSlugName}/contents/delete`, {
    ...init,
    method: 'DELETE',
    body: JSON.stringify({ contentIds }),
  });
};

/**
 * POST /api/v1/campaigns/:campaignSlugName/assets
 *
 * Upload assets to a campaign
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.step - The step in the campaign configuration
 * @param req.field - The field in the campaign configuration
 * @param req.files - The FileList to be uploaded
 */
export const uploadCreatorsCampaignAssets = async (
  req: UploadCampaignAssetsRequest,
  init?: RequestInit,
): Promise<UploadCampaignAssetsResponse> => {
  const body = new FormData();
  body.append('step', req.step);
  body.append('field', req.field);
  body.append('context', req.context);
  body.append('contextId', req.contextId);
  req.files.forEach((file, i) => body.append(`file${++i}`, file));

  // TODO: PD-252281: Replace `passthrough` with `creatorsCampaignsFetch` after the endpoint is available in the gateway
  const res = await passthroughFetch(
    `/api/soco/campaigns/${req.campaignSlugName}/assets`,
    { ...init, method: 'POST', body },
    true,
  );

  return res.json<UploadCampaignAssetsResponse>();
};

/**
 * GET /api/v1/campaigns/
 *
 * Get Creators Campaigns member list by campaignSlugName
 * @param req.searchTerm -  The search term to use
 * @param req.pageNumber - The page number for pagination
 * @param req.pageSize - The page size for pagination
 * @param req.from - The start date in Unix timestamp milliseconds
 * @param req.to - The end date in Unix timestamp milliseconds
 */
export const getCreatorsCampaignsList = async (
  req: ActiveCreatorsCampaignsListRequest,
  init?: RequestInit,
): Promise<CreatorsCampaignListResponse> => {
  const params = makeParams(req);
  const res = await creatorsCampaignsFetch(`?${params}`, {
    ...init,
    method: 'GET',
  });
  const activeCreatorsCampaignListResults = await res.json<{
    data: CreatorsCampaignListResponse;
  }>((res) => ({
    data: deserializeActiveCreatorsCampaignsList(res.data),
  }));

  return activeCreatorsCampaignListResults.data;
};

/**
 * GET /api/v1/campaigns/:campaignSlugName/proposals/creator-portal-invite-link/:creatorId
 *
 * Get Creators Campaigns Creator Portal Link
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.creatorsId - The unique identifier for the creator
 */

export const getCreatorPortalLink = async (
  req: { campaignSlugName: string; creatorId: string },
  init?: RequestInit,
): Promise<CreatorPortalInviteLinkResponse> => {
  const { campaignSlugName, creatorId } = req;
  const res = await creatorsCampaignsFetch(
    `/${campaignSlugName}/proposals/creator-portal-invite-link/${creatorId}`,
    {
      ...init,
      method: 'GET',
    },
  );

  const portalLinkResult = await res.json<{
    data: CreatorPortalInviteLinkResponse;
  }>();

  return portalLinkResult.data;
};

/**
 * GET /api/v1/campaigns/:campaignSlugName/proposals/creator-portal-invite-link/export
 *
 * Get Creators Campaigns links details CSV campaignSlugName
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.creatorsIds - The list of creators to export the links for, if empty all links will be exported
 */
export const exportCreatorPortalLinkCSV = async (
  { campaignSlugName, creatorsIds = [] }: ExportCreatorPortalLinkCSVRequest,
  init?: RequestInit,
): Promise<Blob | undefined> => {
  // TODO: PD-250946: Remove this function once all campaign endpoints are made available in the gateway
  const res = await passthroughFetch(
    `/api/soco/campaigns/${campaignSlugName}/proposals/creator-portal-invite-link/export`,
    {
      ...init,
      method: 'POST',
      body: JSON.stringify({ influencerIds: creatorsIds }),
    },
  );
  return res.blob();
};

/**
 * POST /api/v1/campaigns/:campaignSlugName/proposals/creator-portal-invite-link
 *
 * Get Creators Campaigns links details Json campaignSlugName
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.creatorsIds - The list of creators to export the links for, if empty all links will be exported
 */
export const getCreatorPortalLinks = async (
  { campaignSlugName, creatorsIds = [] }: ExportCreatorPortalLinkCSVRequest,
  init?: RequestInit,
): Promise<CreatorPortalLinksResponse[]> => {
  // TODO: PD-250946: Remove this function once all campaign endpoints are made available in the gateway
  const res = await passthroughFetch(
    `/api/soco/campaigns/${campaignSlugName}/proposals/creator-portal-invite-link`,
    {
      ...init,
      method: 'POST',
      body: JSON.stringify({ influencerIds: creatorsIds }),
    },
  );
  const portalLinkResult = await res.json<CreatorPortalLinksResponse[]>();

  return portalLinkResult;
};

/**
 * GET /api/v1/campaigns/:campaignSlugName/content-approval/link/:creatorsId
 *
 * Get Creator Content Approval Link
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.creatorsId - The unique identifier for the creator
 */
export const getCreatorContentApprovalLink = async (
  { campaignSlugName, creatorId }: GetCreatorContentApprovalLinkRequest,
  init?: RequestInit,
): Promise<CreatorContentApprovalLinkResponse> => {
  const res = await creatorsCampaignsFetch(
    `/${campaignSlugName}/content-approval/link/${creatorId}`,
    {
      ...init,
      method: 'GET',
    },
  );

  const linkResult = await res.json<{
    data: CreatorContentApprovalLinkResponse;
  }>();

  return linkResult.data;
};

/**
 * POST /api/v1/campaigns/:campaignSlugName/content-approval/link/export/csv
 *
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.creatorsIds - The list of creators to export the links for, if empty all links will be exported
 */
export const exportCreatorContentApprovalLinksCSV = async (
  {
    campaignSlugName,
    creatorIds = [],
  }: ExportCreatorContentApprovalLinkCSVRequest,
  init?: RequestInit,
): Promise<Blob | undefined> => {
  const res = await creatorsCampaignsFetch(
    `/${campaignSlugName}/content-approval/link/export/csv`,
    {
      ...init,
      method: 'POST',
      body: JSON.stringify({ creatorIds }),
    },
  );

  return res.blob();
};

/**
 * GET /api/v1/campaigns/:campaignSlugName/content-approval/creators
 *
 * Get List of Creators Content for Approval
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.pageNumber - The page number
 * @param req.sortCriteria - The sort criteria by creator username, approval status and last updated
 * @param req.sortOrder - The sort order
 */
export const getContentApprovalCreatorsList = async (
  {
    allCreators,
    campaignSlugName,
    searchTerm,
    sortCriteria,
    sortOrder,
    status,
  }: GetContentApprovalCreatorsListRequest,
  init?: RequestInit,
): Promise<ContentApprovalCreatorsListResponse> => {
  const res = await creatorsCampaignsFetch(
    `/${campaignSlugName}/content-approval/creators?${makeParams({
      allCreators,
      sortCriteria,
      sortOrder,
      searchTerm,
      status,
    })}`,
    {
      ...init,
      method: 'GET',
    },
  );

  const linkResult = await res.json<{
    data: ContentApprovalCreatorsListResponse;
  }>((res) => ({
    data: deserializeContentApprovalCreatorsList(res.data),
  }));

  return linkResult.data;
};

/**
 * POST /api/v1/campaigns/:campaignSlugName/content-approval/creators/export
 * @param campaignSlugName
 * @body creators
 */

export async function exportContentApprovalCreators(
  { campaignSlugName, creatorsInfo }: ExportContentApprovalCreatorsRequest,
  init?: RequestInit,
): Promise<Blob | undefined> {
  const response = await creatorsCampaignsFetch(
    `/${campaignSlugName}/content-approval/creators/export`,
    {
      ...init,
      method: 'POST',
      body: JSON.stringify({ creators: creatorsInfo }),
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers || {}),
      },
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to export content approval creators: ${response.statusText}`,
    );
  }

  return response.blob();
}

/**
 * GET /api/v1/pass-through/api/soco/campaigns/:campaignSlugName/content-approval/contents?influencerId=:creatorId
 *
 * Get Creator Contents Submitted For Content Review
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.creatorId - The unique identifier for the creator
 */
export const getCreatorContentForReview = async (
  {
    campaignSlugName,
    creatorId,
  }: GetCreatorContentApprovalCreatorContentsRequest,
  init?: RequestInit,
): Promise<ContentApprovalContentResponse[]> => {
  // TODO: PD-271348 - Use actual gateway endpoint instead of passthrough
  const res = await passthroughFetch(
    `/api/soco/campaigns/${campaignSlugName}/content-approval/contents?influencerId=${creatorId}`,
    {
      ...init,
      method: 'GET',
    },
  );

  return res.json<ContentApprovalContentResponse[]>(
    (res: SerializedArray<ContentApprovalContentResponse>) =>
      deserializeContentReviewContentsList(res),
  );
};

/**
 * PATCH /api/v1/pass-through/api/soco/campaigns/:campaignSlugName/content-approval/contents/:contentId/approved
 *
 * Approve creator's submitted content
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.contentId - The unique identifier for the content to be approved
 * @param [req.feedback] - Optional comment sent with the approval
 * @param req.version - Version of the content to be approved
 */
export const approveContent = async (
  { campaignSlugName, contentId, feedback, version }: ApproveContentRequest,
  init?: RequestInit,
): Promise<ApproveContentResponse> => {
  // TODO: PD-271348 - Use actual gateway endpoint instead of passthrough
  const res = await passthroughFetch(
    `/api/soco/campaigns/${campaignSlugName}/content-approval/contents/${contentId}/approved`,
    {
      ...init,
      body: JSON.stringify({
        // feedback may be an empty string, which we don't want to save
        feedback: isEmptyValue(feedback) ? null : feedback,
        version,
      }),
      method: 'PATCH',
    },
  );

  return res.json<ApproveContentResponse>();
};

/**
 * POST /api/v1/pass-through/api/soco/campaigns/:campaignSlugName/content-approval/contents/:contentId/feedback
 *
 * Send comment/feedback for creator's submitted content
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.contentId - The unique identifier for the content being commented on
 * @param req.feedback - Comment/feedback for the content
 * @param req.version - Version of the content to be commented on
 */
export const sendComment = async (
  { campaignSlugName, contentId, feedback, version }: SendCommentRequest,
  init?: RequestInit,
): Promise<SendCommentResponse> => {
  // TODO: PD-271348 - Use actual gateway endpoint instead of passthrough
  const res = await passthroughFetch(
    `/api/soco/campaigns/${campaignSlugName}/content-approval/contents/${contentId}/feedback`,
    {
      ...init,
      body: JSON.stringify({
        // feedback may be an empty string, which we don't want to save
        feedback: isEmptyValue(feedback) ? null : feedback,
        version,
      }),
      method: 'POST',
    },
  );

  return res.json<SendCommentResponse>(deserializeSendCommentResponse);
};

/**
 * GET /api/v1/campaigns/:campaignSlugName/affiliate/exportCsv
 *
 * Get Creators Campaigns affiliate details CSV campaignSlugName
 * @param req.campaignSlugName - The unique identifier for the campaign
 */
export const exportAffiliateCreators = async (
  { campaignSlugName }: ExportCreatorPortalLinkCSVRequest,
  init?: RequestInit,
): Promise<Blob | undefined> => {
  // TODO: PD-250946: Remove this function once all campaign endpoints are made available in the gateway
  const res = await passthroughFetch(
    `/api/soco/campaigns/${campaignSlugName}/affiliate/exportCsv`,
    { ...init, method: 'GET' },
  );
  return res.blob();
};

/**
 * GET /api/v1/campaigns/:campaignSlugName/proposals/export
 *
 * Get Creators Campaigns Proposals details CSV campaignSlugName
 * @param req.campaignSlugName - The unique identifier for the campaign
 */
export const getCreatorsCampaignsProposalsDetailsCSV = async (
  req: CreatorsCampaignsBasicRequest,
  init?: RequestInit,
): Promise<Blob | undefined> => {
  const { campaignSlugName } = req;
  const res = await creatorsCampaignsFetch(
    `/${campaignSlugName}/proposals/export`,
    {
      ...init,
      method: 'GET',
    },
  );

  return res.blob();
};

/**
 * GET /api/v1/pass-through/api/soco/campaigns/:campaignSlugName/tiktok/tto/spark-codes/media?refresh=:refresh
 *
 * Get a list of medias with Spark Ads for this campaign
 * @param req.campaignSlugName - The unique identifier for the campaign
 */
export const getSparkCodeMediaIds = async (
  req: GetSparkCodeMediaIdsRequest,
  init?: RequestInit,
): Promise<SparkCodeMedia[]> => {
  const { campaignSlugName, refresh } = req;
  // TODO PD-255099: Remove after endpoint is made available in gateway
  const res = await passthroughFetch(
    `/api/soco/campaigns/${campaignSlugName}/tiktok/tto/spark-codes/media?refresh=${refresh}`,
    {
      ...init,
      method: 'GET',
    },
  );
  const response = await res.json<SparkCodeMediaResponse>();

  return response.medias ? response.medias : [];
};

/**
 * POST /api/v1/pass-through/api/soco/campaigns/:campaignSlugName/tiktok/tto/spark-codes
 *
 * Get spark code for a given video
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.body.videoId - The identifier for the TikTok video
 * @param req.body.refresh - Whether to fetch from cache
 */
export const getCampaignSparkCode = async (
  req: GetCampaignSparkCodeRequest,
  init?: RequestInit,
): Promise<CampaignSparkCodesInfo> => {
  const { campaignSlugName, refresh, videoId } = req;
  // TODO PD-255099: Remove after endpoint is made available in gateway
  const res = await passthroughFetch(
    `/api/soco/campaigns/${campaignSlugName}/tiktok/tto/spark-codes`,
    {
      ...init,
      method: 'POST',
      body: JSON.stringify({ videoId, refresh }),
    },
  );
  const data = await res.json<CampaignSparkCodesInfo>(
    deserializeCampaignSparkCodeResponse,
  );
  return data;
};

/**
 * POST api/v1/pass-through/api/soco/campaigns/:campaignSlugName/tiktok/link/video
 *
 * Link a tiktok video to a tto campaign
 * @param req.campaignSlugName - The unique identifier for the Affable campaign
 * @param req.campaignId - The unique identifier for the campaign
 * @param req.body.videoId - The identifier for the TikTok video
 */
export const linkTiktokVideoToTTOCampaign = async (
  req: LinkTiktokVideoToTTOCampaignRequest,
  init?: RequestInit,
): Promise<LinkTiktokVideoToTTOCampaignResponse> => {
  const { campaignId, campaignSlugName, videoId } = req;
  // TODO PD-255099: Remove after endpoint is made available in gateway
  const res = await passthroughFetch(
    `/api/soco/campaigns/${campaignSlugName}/tiktok/link/video`,
    {
      ...init,
      method: 'POST',
      body: JSON.stringify({ videoId, campaignId }),
    },
  );

  return res.json<LinkTiktokVideoToTTOCampaignResponse>();
};

/**
 * POST /api/soco/campaigns/:campaignSlugName/tiktok/spark-codes/request
 *
 * Request authorization for spark code for a certain video
 * @param req.campaignSlugName - The campaign slug name
 * @param req.body.videoId - The identifier for the TikTok video
 * @param req.body.authorizationDays - The number of days to authorize the Spark Ads for
 */
export const postRequestSparkCodeAuthorization = async (
  req: RequestSparkCodeRequest,
  init?: RequestInit,
): Promise<{ message: string }> => {
  const { authorizationDays, campaignSlugName, itemId } = req;

  // TODO PD-255099: Remove after endpoint is made available in gateway
  const res = await passthroughFetch(
    `/api/soco/campaigns/${campaignSlugName}/tiktok/tto/spark-codes/request`,
    {
      ...init,
      method: 'POST',
      body: JSON.stringify({ videoId: itemId, authorizationDays }),
    },
  );

  return res.json<{ message: string }>();
};

/**
 * GET /api/soco/organization/tiktok-ad-accounts
 *
 * Get TikTok Ad accounts for an organization
 */
export const getTiktokAdAccounts = async (
  init?: RequestInit,
): Promise<TikTokAdAccountsResponse> => {
  // TODO PD-257470: Remove after endpoint made available in gateway
  const res = await passthroughFetch(
    `/api/soco/organization/tiktok-ad-accounts`,
    {
      ...init,
      method: 'GET',
    },
  );

  return res.json<TikTokAdAccountsResponse>();
};

/**
 * POST /api/soco/organization/tiktok-ads/authorize-video
 *
 * Authorize TikTok Ad accounts for a video, identified by its Spark Code
 * @param req.sparkCode - the video's Spark Code
 * @param req.advertiserIds - the advertiser IDs to authorize
 */
export const applySparkCode = async (
  req: ApplySparkCodeRequest,
  init?: RequestInit,
): Promise<void> => {
  // TODO PD-257470: Remove after endpoint made available in gateway
  await passthroughFetch(`/api/soco/organization/tiktok-ads/authorize-video`, {
    ...init,
    method: 'POST',
    body: JSON.stringify({
      authCode: req.sparkCode,
      advertiserIds: req.advertiserIds,
    }),
  });
};

/*
 * GET /api/soco/campaigns/:campaignSlugName/affiliates
 *
 * Get Creators Campaigns Affiliates
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.pageNumber - The page number to use
 * @param req.pageSize - The page size to use
 * @param req.searchTerm - The search term to use
 * @param req.allInfluencers - Whether to include all influencers
 */
export const getCreatorsCampaignsAffiliatesList = async (
  req: CreatorsCampaignsAffiliatesListRequest,
  init?: RequestInit,
): Promise<CreatorsCampaignsAffiliatesListResponse> => {
  const { campaignSlugName, ...queryParams } = req;

  const res = await creatorsCampaignsFetch(
    `/${campaignSlugName}/affiliates?${makeParams(queryParams)}`,
    {
      ...init,
      method: 'GET',
    },
  );

  const result = await res.json<{
    data: CreatorsCampaignsAffiliatesListResponse;
  }>();

  return result.data;
};

/*
 * GET /api/soco/campaigns/:campaignSlugName/affiliates/google-analytics
 *
 * Get Creators Campaigns Affiliates
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.pageNumber - The page number to use
 * @param req.pageSize - The page size to use
 * @param req.searchTerm - The search term to use
 * @param req.allInfluencers - Whether to include all influencers
 */
export const getCreatorsCampaignsAffiliatesGAConfigurationList = async (
  req: CreatorsCampaignsAffiliatesGAConfigurationRequest,
  init?: RequestInit,
): Promise<CreatorsCampaignsAffiliatesGoogleAnalyticsConfigurationResponse> => {
  const { campaignSlugName, ...queryParams } = req;

  const res = await creatorsCampaignsFetch(
    `/${campaignSlugName}/affiliates/google-analytics?${makeParams(queryParams)}`,
    {
      ...init,
      method: 'GET',
    },
  );

  const result = await res.json<{
    data: CreatorsCampaignsAffiliatesGoogleAnalyticsConfigurationResponse;
  }>();

  return result.data;
};

/**
 * GET /api/campaigns/:campaignSlugName/affiliates/:affiliateCode/details'
 *
 * Get Creators Campaigns Affiliate Orders details
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.affiliateCode - The affiliate code for the affiliate
 */
export const getCreatorsCampaignsAffiliateOrdersDetails = async (
  req: CreatorsCampaignsAffiliateOrdersRequest,
  init?: RequestInit,
): Promise<CreatorsCampaignsAffiliateOrdersResponse> => {
  const { affiliateCode, campaignSlugName, from, pageNumber, pageSize, to } =
    req;

  const res = await creatorsCampaignsFetch(
    `/${campaignSlugName}/affiliates/${affiliateCode}/details?${makeParams({
      from: isDefined(from) ? from.getTime() : undefined,
      to: isDefined(to) ? to.getTime() : undefined,
      pageNumber,
      pageSize,
    })}`,
    {
      ...init,
      method: 'GET',
    },
  );

  const result = await res.json<{
    data: CreatorsCampaignsAffiliateOrdersResponse;
  }>();

  return result.data;
};

/**
 * POST /api/v1/campaigns/:campaignSlugName/workflow/impact
 *
 * Link Impact.com program to campaign
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.programId - The Impact.com program ID to link
 */
export const linkImpactProgramToCampaign = async (
  req: LinkImpactProgramRequest,
  init?: RequestInit,
): Promise<LinkedImpactProgramDto> => {
  const { campaignSlugName, programId } = req;
  const body: LinkImpactProgramRequestBody = { programId };

  const res = await creatorsCampaignsFetch(
    `/${campaignSlugName}/workflow/impact`,
    {
      ...init,
      method: 'POST',
      body: JSON.stringify(body),
    },
  );

  const linkedImpactProgramResponse = await res.json<{
    data: LinkedImpactProgramDto;
  }>();

  return linkedImpactProgramResponse.data;
};

/**
 * GET /api/v1/campaigns/:campaignSlugName/workflow/impact
 *
 * Get linked Impact.com program for campaign
 * @param req.campaignSlugName - The unique identifier for the campaign
 */
export const getLinkedImpactProgram = async (
  req: ImpactWorkflowRequest,
  init?: RequestInit,
): Promise<LinkedImpactProgramDto | null> => {
  const { campaignSlugName } = req;

  const res = await creatorsCampaignsFetch(
    `/${campaignSlugName}/workflow/impact`,
    {
      ...init,
      method: 'GET',
    },
  );

  const linkedImpactProgramResponse = await res.json<{
    data: LinkedImpactProgramDto | null;
  }>();

  return linkedImpactProgramResponse.data;
};

/**
 * Get partner mappings for Impact.com workflow
 *
 * GET /api/v1/campaigns/:campaignSlugName/workflow/impact/partners/mapping
 */
export const getPartnerMappings = async (
  req: GetPartnerMappingsRequest,
  init?: RequestInit,
): Promise<GetPartnerMappingsResponse> => {
  const { campaignSlugName } = req;

  const res = await creatorsCampaignsFetch(
    `/${campaignSlugName}/workflow/impact/partners/mapping`,
    {
      ...init,
      method: 'GET',
    },
  );

  return res.json<GetPartnerMappingsResponse>();
};

/**
 * GET /api/soco/campaigns/:campaignSlugName/affiliates/stats
 *
 * Get Creators Campaigns Affiliates
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.granularity - The granularity of the affiliate stats data if needed.
 * If this field is provided, the response would include the timeseries field.
 * @param req.startDate - The start date (inclusive) for the time interval for which to get the data for
 * @param req.endDate - The end date (inclusive) for the time interval for which to get the data for
 */
export const getCreatorsCampaignsAffiliatesAggregateStats = async (
  req: GetCreatorsCampaignAffiliatesStatsRequest,
  init?: RequestInit,
): Promise<CreatorsCampaignsAffiliatesAggregateStatsResponse> => {
  const { campaignSlugName, ...queryParams } = req;
  const res = await creatorsCampaignsFetch(
    `/${campaignSlugName}/affiliates/stats?${makeParams(queryParams)}`,
    {
      ...init,
      method: 'GET',
    },
  );

  const result = await res.json<{
    data: CreatorsCampaignsAffiliatesAggregateStatsResponse;
  }>();

  return result.data;
};

/**
 * Map creators to Impact.com partners for a specific campaign
 *
 * POST /api/v1/campaigns/$campaignSlugName/workflow/impact/partners
 */
export const mapCreatorsToImpactPartners = async (
  req: ImpactMappingRequest,
  init?: RequestInit,
): Promise<LinkImpactPartnerResponse> => {
  const { campaignSlugName, linkRequests } = req;

  const response = await creatorsCampaignsFetch(
    `/${campaignSlugName}/workflow/impact/partners`,
    {
      ...init,
      method: 'POST',
      body: JSON.stringify({ linkRequests }),
    },
  );
  return response.json<LinkImpactPartnerResponse>();
};

/**
 * GET /api/v1/campaigns/:campaignSlugName/workflow/impact/partners
 *
 * Get connected Impact partners for a specific creator in a campaign
 */
export const getConnectedImpactPartners = async (
  req: GetConnectedImpactPartnersRequest,
  init?: RequestInit,
): Promise<GetConnectedImpactPartnersResponse> => {
  const { campaignSlugName, ...queryParams } = req;

  const response = await creatorsCampaignsFetch(
    `/${campaignSlugName}/workflow/impact/partners?${makeParams(queryParams)}`,
    {
      ...init,
      method: 'GET',
    },
  );

  const connectedPartnersResponse =
    await response.json<GetConnectedImpactPartnersResponse>();

  return connectedPartnersResponse;
};

/**
 * DELETE /api/v1/campaigns/:campaignSlugName/workflow/impact/partners/:influencerId/:platform
 *
 * Unlink creator from Impact partner for a specific campaign
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.influencerId - The unique identifier for the creator/influencer
 * @param req.platform - The social media platform (e.g., 'INSTAGRAM', 'TIKTOK')
 */
export const unlinkCreatorFromImpactPartner = async (
  req: UnlinkCreatorFromImpactPartnerRequest,
  init?: RequestInit,
): Promise<UnlinkCreatorFromImpactPartnerResponse> => {
  const { campaignSlugName, influencerId, platform } = req;

  const response = await creatorsCampaignsFetch(
    `/${campaignSlugName}/workflow/impact/partners/${influencerId}/${platform}`,
    {
      ...init,
      method: 'DELETE',
    },
  );

  return response.json<UnlinkCreatorFromImpactPartnerResponse>();
};

/**
 * POST /api/v1/campaigns/:campaignSlugName/influencers/media
 * Upload media codes for a creator in a campaign
 */
export const uploadCreatorMedia = async (
  {
    campaignSlugName,
    mediaCodes,
    platform,
    username,
  }: UploadCreatorMediaRequest,
  init?: RequestInit,
): Promise<UploadCreatorMediaResponse> => {
  const response = await creatorsCampaignsFetch(
    `/${campaignSlugName}/influencers/media?${makeParams({ platform })}`,
    {
      ...init,
      method: 'POST',
      body: JSON.stringify({
        mediaCodes,
        username,
      }),
    },
  );

  return response.json<UploadCreatorMediaResponse>();
};

/**
 * PATCH /api/v1/campaigns/:campaignSlugName/creators/bulk
 *
 * Bulk update campaign creators' metadata (including fixed pay)
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.creators - Array of creators with metadata to update
 */
export const bulkUpdateCreatorsMetadata = async (
  req: BulkUpdateCreatorsRequest,
  init?: RequestInit,
): Promise<BulkUpdateCreatorsResponse> => {
  const { campaignSlugName, creators } = req;

  const response = await creatorsCampaignsFetch(
    `/${campaignSlugName}/creators/bulk`,
    {
      ...init,
      method: 'PATCH',
      body: JSON.stringify({ creators }),
    },
  );

  const json = await response.json<{ data: BulkUpdateCreatorsResponse }>();
  return json.data;
};

/**
 * PATCH /api/v1/client-users/disable-sensitive-campaign-links-warning
 *
 * Update Creators Campaigns Sensitive Links Preference
 */
export const disableCreatorsCampaignSensitiveLinksWarning = async (
  init?: RequestInit,
): Promise<void> => {
  await serializedGatewayFetch(
    'affable_gateway',
    `${CLIENT_USERS_DEFAULT}/disable-sensitive-campaign-links-warning`,
    {
      ...init,
      method: 'PATCH',
    },
  );
};
